import { useEffect, useRef, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { UseSignalRHubOptions, UseSignalRHubResult } from './use-signalr-hub.types';

/**
 * Generic hook that manages the lifecycle of a SignalR HubConnection.
 *
 * This hook is transport-layer only — it has no knowledge of domains, query keys,
 * or app settings. Domain-specific hubs should wrap this hook and register their
 * own event handlers via `connection.on(eventName, handler)`.
 *
 * @example
 * ```ts
 * const { connection, connectionState } = useSignalRHub({
 *   hubUrl: apiUrlBuilder('hubs/amendments', applicationApiUrl),
 *   getAccessToken: () => user?.access_token,
 * });
 *
 * useEffect(() => {
 *   if (!connection) return;
 *   connection.on('AmendmentBatchCompleted', handleComplete);
 *   return () => connection.off('AmendmentBatchCompleted', handleComplete);
 * }, [connection]);
 * ```
 */
export function useSignalRHub({
  hubUrl,
  getAccessToken,
}: UseSignalRHubOptions): UseSignalRHubResult {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(
    HubConnectionState.Disconnected,
  );
  const [reconnectCount, setReconnectCount] = useState(0);

  // Keep the token factory current without causing a reconnect on every render.
  // SignalR calls this ref before each (re)connection attempt, so it always
  // receives the latest token without being a reactive dependency.
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  });

  useEffect(() => {
    if (!hubUrl) return;

    // isActive prevents state updates if the component unmounts before start() resolves.
    let isActive = true;

    // Configure the connection — auth token is fetched fresh on every (re)connect.
    // withCredentials: false stops the browser sending cookies; auth is Bearer token only.
    const conn = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => getAccessTokenRef.current() ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect() // retries with exponential backoff on disconnect
      .configureLogging(LogLevel.Warning)
      .build();

    // Mirror SignalR lifecycle events into React state so components can react to them.
    conn.onreconnecting(() => {
      if (isActive) setConnectionState(HubConnectionState.Reconnecting);
    });
    conn.onreconnected(() => {
      if (isActive) {
        setConnectionState(HubConnectionState.Connected);
        // Incrementing tells domain hooks to re-invoke their server-side subscribe
        // calls — the server drops all group memberships when a connection is lost.
        setReconnectCount((c) => c + 1);
      }
    });
    conn.onclose(() => {
      if (isActive) setConnectionState(HubConnectionState.Disconnected);
    });

    // Open the WebSocket. Only expose the connection after start() resolves
    // so consumers can safely call invoke() without extra state checks.
    conn
      .start()
      .then(() => {
        if (isActive) {
          setConnectionState(HubConnectionState.Connected);
          setConnection(conn);
        }
      })
      .catch(() => {
        if (isActive) setConnectionState(HubConnectionState.Disconnected);
      });

    return () => {
      isActive = false;
      setConnection(null);
      setConnectionState(HubConnectionState.Disconnected);
      conn.stop();
    };
  }, [hubUrl]);

  return { connection, connectionState, reconnectCount };
}
